import { useState, useEffect, useMemo, useRef } from "react";
import { LOADING_MESSAGES, LOADING_PODCASTS } from "@/data/mockData";
import { Radio } from "lucide-react";

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function LoadingState() {
  const shuffledMessages = useMemo(() => shuffle(LOADING_MESSAGES), []);
  const podQueueRef = useRef<string[]>(shuffle(LOADING_PODCASTS));
  const podCursorRef = useRef(0);
  const [msgIndex, setMsgIndex] = useState(0);
  const [currentPod, setCurrentPod] = useState<string>(() => podQueueRef.current[0]);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const startTime = Date.now();

    const msgTimer = setInterval(() => {
      setMsgIndex((i) => (i + 1) % shuffledMessages.length);
    }, 1800);
    const podTimer = setInterval(() => {
      podCursorRef.current += 1;
      if (podCursorRef.current >= podQueueRef.current.length) {
        // Reshuffle, avoiding immediate repeat of the last podcast
        const last = podQueueRef.current[podQueueRef.current.length - 1];
        let next = shuffle(LOADING_PODCASTS);
        if (next[0] === last && next.length > 1) {
          [next[0], next[next.length - 1]] = [next[next.length - 1], next[0]];
        }
        podQueueRef.current = next;
        podCursorRef.current = 0;
      }
      setCurrentPod(podQueueRef.current[podCursorRef.current]);
    }, 1200);


    // Fast surge to ~70%, then crawl — perceived speed boost
    const progTimer = setInterval(() => {
      setProgress((p) => {
        if (p < 70) return p + 12 + Math.random() * 6; // rockets to 70 in ~600ms
        if (p < 88) return p + 0.8 + Math.random() * 0.8;
        if (p < 95) return p + 0.15 + Math.random() * 0.2;
        return p;
      });
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 120);


    return () => {
      clearInterval(msgTimer);
      clearInterval(podTimer);
      clearInterval(progTimer);
    };
  }, []);

  const displayProgress = Math.min(Math.round(progress), 95);

  return (
    <div className="flex flex-col items-center justify-center py-16 sm:py-24 gap-5 px-4">
      {/* Pulsing icon */}
      <div className="relative">
        <Radio className="w-10 h-10 sm:w-12 sm:h-12 text-primary animate-bounce-subtle" />
        <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
      </div>

      {/* Messages */}
      <div className="text-center space-y-2 min-h-[56px]">
        <p
          className="font-mono text-sm sm:text-lg text-foreground animate-fade-in"
          key={msgIndex}
        >
          {shuffledMessages[msgIndex]}
        </p>
        <p
          className="font-mono text-xs sm:text-sm text-muted-foreground animate-fade-in"
          key={`pod-${currentPod}`}
        >
          📻 {currentPod}
        </p>

      </div>

      {/* Progress bar with percentage */}
      <div className="w-48 sm:w-64 space-y-1.5">
        <div className="h-2 bg-muted retro-border overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-400 ease-out"
            style={{ width: `${displayProgress}%` }}
          />
        </div>
        <p className="font-mono text-[10px] sm:text-xs text-muted-foreground text-center">
          {displayProgress}% complete
        </p>
      </div>

      {/* Timeout / reassurance messages */}
      {elapsed >= 8 && elapsed < 20 && (
        <p className="font-mono text-[10px] sm:text-xs text-muted-foreground/70 animate-fade-in text-center max-w-xs">
          Hang tight — we're scanning thousands of podcast transcripts for the best matches…
        </p>
      )}
      {elapsed >= 20 && elapsed < 40 && (
        <p className="font-mono text-[10px] sm:text-xs text-accent animate-fade-in text-center max-w-xs">
          Still working! Deep searches take a bit longer — ranking the most relevant episodes for you.
        </p>
      )}
      {elapsed >= 40 && (
        <p className="font-mono text-[10px] sm:text-xs text-accent font-bold animate-fade-in text-center max-w-xs">
          Almost there — this is a thorough search. Results are coming shortly!
        </p>
      )}
    </div>
  );
}
